import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { FrakLogo } from "../../components/FrakLogo";
import { useCopy } from "../copy";
import { FLOAT_SHADOW, LOOK } from "../look";
import { useStage } from "../stage";
import { Sfx } from "../ui/Sfx";
import { Headline, Kicker } from "../ui/Split";

/**
 * End card — the partner page's closing CTA, verbatim ("Prenez une
 * longueur d'avance sur la Recommendation Economy."), the Founding Brands
 * offer, the waiting-list button and the URL. White after the blue proof
 * scene; a quiet hold at the end so a paused last frame reads as a
 * finished CTA (and as a clean LinkedIn end frame).
 */
export const Cta: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const stage = useStage();
  const c = copy.cta;
  const wide = stage.shape === "wide";

  const logo = spring({
    frame,
    fps,
    config: { damping: 12, stiffness: 200, mass: 0.7 },
  });
  const sub = spring({
    frame: frame - 26,
    fps,
    config: { damping: 18, stiffness: 180 },
  });
  const button = spring({
    frame: frame - 34,
    fps,
    config: { damping: 13, stiffness: 200, mass: 0.7 },
  });
  const sheen = interpolate(frame, [58, 80], [-30, 130], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <>
      <AbsoluteFill
        style={{
          background: `radial-gradient(80% 70% at 50% 0%, ${LOOK.sky} 0%, ${LOOK.white} 70%)`,
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: wide ? "0 260px" : "0 70px",
        }}
      >
        <div style={{ scale: logo, marginBottom: 40 }}>
          <FrakLogo width={wide ? 240 : 220} />
        </div>
        <Kicker text={c.kicker} at={4} />
        {/* One sentence, so it balances over 2 lines (wide) or 3 (square/feed). */}
        <Headline
          text={`${c.line1} ${c.line2}`}
          start={6}
          size={wide ? 96 : 76}
          stagger={2}
        />
        <div
          style={{
            marginTop: 30,
            fontFamily: LOOK.body,
            fontWeight: LOOK.medium,
            fontSize: 40,
            color: LOOK.inkSoft,
            opacity: sub,
            translate: `0 ${(1 - sub) * 20}px`,
          }}
        >
          {c.sub}
        </div>

        <div
          style={{
            marginTop: 50,
            display: "flex",
            flexDirection: wide ? "row" : "column",
            alignItems: "center",
            gap: wide ? 34 : 26,
            opacity: button,
            translate: `0 ${(1 - button) * 50}px`,
          }}
        >
          <div
            style={{
              position: "relative",
              overflow: "hidden",
              padding: "30px 56px",
              borderRadius: 999,
              background: LOOK.blue,
              color: LOOK.white,
              fontFamily: LOOK.body,
              fontWeight: LOOK.bold,
              fontSize: 40,
              boxShadow: FLOAT_SHADOW,
              whiteSpace: "nowrap",
            }}
          >
            {c.button} →
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${sheen}%`,
                width: "20%",
                background:
                  "linear-gradient(100deg, rgba(255,255,255,0), rgba(255,255,255,0.4), rgba(255,255,255,0))",
              }}
            />
          </div>
          <div
            style={{
              fontFamily: LOOK.display,
              fontWeight: LOOK.bold,
              fontSize: 44,
              letterSpacing: -1,
              color: LOOK.ink,
            }}
          >
            {c.url}
          </div>
        </div>
      </AbsoluteFill>
      <Sfx sound="pop" at={34} volume={0.8} />
      {/* Peaks as the sheen crosses the button (58 → 80). */}
      <Sfx sound="sparkle" at={66} />
    </>
  );
};
